import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Input from '@mui/material/Input';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import QRCode from 'react-qr-code';
import {
  Recording,
  TableRecordingsRow,
  MatrixLevel,
  RecordingAnalysis,
  MeetingPurpose,
  MEETING_PURPOSES,
} from './TableRecordingsRow';
import { RecapReportView } from './RecapReport';
import { EnhancedTableHead } from './TableUtils';
import HelpTip from './HelpTip';
import { ApiFetch } from '../App';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { AUDIO_ACCEPT, isIOS } from './audioInput';

interface UploadSession {
  id: string;
  label: string;
  lang: string;
  type: 'managed' | 'self-service' | 'rmet';
  status: 'open' | 'closed';
  createdAt: string;
  closedAt?: string;
  passcode?: string;
  createdBy?: string;
}

const uploadPageUrl = (sessionId: string): string =>
  `${window.location.origin}/upload/${sessionId}`;

pdfMake.vfs = pdfFonts.vfs;
pdfMake.fonts = {
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics:
      'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

type FileStatus = 'idle' | 'uploading' | 'analyzing' | 'done' | 'error';

interface FileEntry {
  file: File;
  numParticipants: string;
  meetingPurpose: MeetingPurpose;
  status: FileStatus;
}

interface TableRecordingsProps {
  rows: Recording[];
  setRecordings: (recordings: Recording[]) => void;
  apiFetch: ApiFetch;
  /** Shows the admin-only Owner column. */
  isAdmin: boolean;
}

// ------------------------------------------------------------
// Legacy (v2.1) report rendering — only for records generated before the ReCap v3
// engine, which are kept as they were produced. New reports use RecapReportView.
// ------------------------------------------------------------

// Items where low is the good outcome (inverted polarity)
const NEGATIVE_ITEMS = new Set(['social_pain', 'systemic_silence']);

const itemLevelColor = (level: MatrixLevel, itemKey: string): 'success' | 'warning' | 'error' => {
  const isNegative = NEGATIVE_ITEMS.has(itemKey);
  const isGood = isNegative ? level === 'Low' : level === 'High';
  const isBad = isNegative ? level === 'High' : level === 'Low';
  if (isGood) return 'success';
  if (isBad) return 'error';
  return 'warning';
};

const deriveStatusMatrix = (analysis: RecordingAnalysis): string | undefined => {
  if (analysis.statusMatrix) return analysis.statusMatrix;
  if (!analysis.matrix) return undefined;
  const allItems = analysis.matrix.flatMap((area) => area.items);
  const color = (level: MatrixLevel, itemKey: string) => {
    const neg = NEGATIVE_ITEMS.has(itemKey);
    if (neg ? level === 'High' : level === 'Low') return 'red';
    if (neg ? level === 'Low' : level === 'High') return 'green';
    return 'yellow';
  };
  if (allItems.some((item) => color(item.level, item.itemKey) === 'red')) return 'at_risk';
  const socialPain = allItems.find((item) => item.itemKey === 'social_pain');
  if (socialPain?.level === 'Medium') return 'at_risk';
  const yellowCount = allItems.filter(
    (item) => color(item.level, item.itemKey) === 'yellow'
  ).length;
  return yellowCount <= 1 ? 'solid' : 'unstructured';
};

const STATUS_MATRIX_COLORS: Record<string, 'success' | 'warning' | 'error'> = {
  solid: 'success',
  unstructured: 'warning',
  at_risk: 'error',
};

const MatrixSection: React.FC<{ matrix: RecordingAnalysis['matrix'] }> = ({ matrix }) => {
  const { t } = useTranslation();
  if (!matrix) return null;
  return (
    <Box paddingTop={3}>
      <Typography variant="h6" paddingBottom={2}>
        {t('transcription.matrix_title')}
      </Typography>
      <Grid container spacing={2}>
        {matrix.map((area) => (
          <Grid key={area.areaKey} size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} paddingBottom={1}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                  {t(`transcription.area_${area.areaKey}`)}
                </Typography>
              </Box>
              {area.items.map((item) => (
                <Box
                  key={item.itemKey}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  paddingBottom={0.5}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {t(`transcription.item_${item.itemKey}`)}
                  </Typography>
                  {item.level && (
                    <Chip
                      size="small"
                      label={t(`transcription.level_${item.level.toLowerCase()}`)}
                      color={itemLevelColor(item.level, item.itemKey)}
                      sx={{
                        width: 96,
                        flexShrink: 0,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                      }}
                    />
                  )}
                </Box>
              ))}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const PDF_LEVEL_COLORS: Record<'success' | 'warning' | 'error', string> = {
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
};

const buildLevelChip = (
  label: string,
  colorName: 'success' | 'warning' | 'error',
  width?: string | number,
  chipWidth = 64
): Content =>
  ({
    table: {
      widths: [chipWidth],
      body: [
        [
          {
            text: label.toUpperCase(),
            fillColor: PDF_LEVEL_COLORS[colorName],
            color: '#ffffff',
            fontSize: 9,
            bold: true,
            alignment: 'center',
            margin: [0, 2, 0, 2] as [number, number, number, number],
          },
        ],
      ],
    },
    layout: 'noBorders',
    width,
  }) as Content;

const buildRecordingPdf = (
  id: string,
  analysis: RecordingAnalysis,
  t: (key: string, opts?: Record<string, string>) => string
): TDocumentDefinitions => {
  const status = deriveStatusMatrix(analysis);
  const content: Content[] = [
    { text: id, style: 'title' },
    ...(status
      ? [
          {
            columns: [
              { text: t('recordings.status_label'), style: 'status', width: 'auto' },
              buildLevelChip(
                t(`transcription.status_matrix_${status}`),
                STATUS_MATRIX_COLORS[status] ?? 'warning',
                'auto',
                110
              ),
            ],
            columnGap: 8,
            margin: [0, 0, 0, 12] as [number, number, number, number],
          } as Content,
        ]
      : []),
    ...(analysis.titleLine ? [{ text: analysis.titleLine, style: 'h6' }] : []),
    ...(analysis.summaryLine ? [{ text: analysis.summaryLine, style: 'h6' }] : []),
    ...(analysis.body?.length
      ? [
          {
            text: analysis.body.join(' '),
            style: 'body1',
            margin: [0, 0, 0, 4] as [number, number, number, number],
          },
        ]
      : []),
    ...(analysis.question
      ? [
          {
            text: analysis.question,
            style: 'body1',
            margin: [0, 0, 0, 12] as [number, number, number, number],
          },
        ]
      : []),
  ];

  if (analysis.matrix) {
    content.push({ text: t('transcription.matrix_title'), style: 'sectionHeader' });
    for (const area of analysis.matrix) {
      content.push({
        columns: [
          { text: t(`transcription.area_${area.areaKey}`), style: 'listHeader', width: '*' },
        ],
        margin: [0, 4, 0, 2],
      } as Content);
      content.push({
        table: {
          widths: [200, 'auto'],
          body: area.items.map((item) => [
            { text: t(`transcription.item_${item.itemKey}`), style: 'body2' },
            item.level
              ? buildLevelChip(
                  t(`transcription.level_${item.level.toLowerCase()}`),
                  itemLevelColor(item.level, item.itemKey)
                )
              : { text: '' },
          ]),
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 8],
      });
    }
  }

  return {
    content,
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { font: 'Roboto', fontSize: 11, lineHeight: 1.4 },
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 4] },
      status: { fontSize: 13, bold: true },
      h6: { fontSize: 13, bold: false, margin: [0, 0, 0, 4] },
      body1: { fontSize: 11 },
      body2: { fontSize: 10 },
      sectionHeader: { fontSize: 13, bold: true, margin: [0, 12, 0, 6] },
      tableLabel: { fontSize: 10, bold: true },
      listHeader: { fontSize: 10, bold: true, margin: [0, 4, 0, 2] },
    },
  };
};

const statusColor = (status: FileStatus): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  if (status === 'uploading') return 'info';
  if (status === 'analyzing') return 'warning';
  if (status === 'done') return 'success';
  if (status === 'error') return 'error';
  return 'default';
};

const POLL_INTERVAL_MS = 5000;

/** Save a base64 PDF returned by the API as a file download. */
const downloadBase64Pdf = (base64: string, filename: string) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const PurposeSelect: React.FC<{
  value: MeetingPurpose;
  onChange: (value: MeetingPurpose) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <Select
      size="small"
      variant="standard"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as MeetingPurpose)}
      renderValue={(v) => `${t('recap.purpose.label')}: ${t(`recap.purpose.${v}`)}`}
      sx={{ minWidth: 220, flexShrink: 0 }}
    >
      {MEETING_PURPOSES.map((p) => (
        <MenuItem key={p} value={p}>
          {t(`recap.purpose.${p}`)}
        </MenuItem>
      ))}
    </Select>
  );
};

export const TableRecordings: React.FC<TableRecordingsProps> = ({
  rows,
  setRecordings,
  apiFetch,
  isAdmin,
}) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = React.useState<number>(0);
  const [titleFilter, setTitleFilter] = React.useState<string>('');
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [analysisModalOpen, setAnalysisModalOpen] = React.useState<Recording | null>(null);
  const [files, setFiles] = React.useState<FileEntry[]>([]);
  const [meetingLoading, setMeetingLoading] = React.useState<boolean>(false);
  const [showLegacy, setShowLegacy] = React.useState(false);
  const [reanalyzeOpen, setReanalyzeOpen] = React.useState(false);
  const [reanalyzePurpose, setReanalyzePurpose] = React.useState<MeetingPurpose>('unspecified');
  const [pdfLoading, setPdfLoading] = React.useState(false);

  // Upload sessions state
  const [sessions, setSessions] = React.useState<UploadSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = React.useState(false);
  const [createSessionOpen, setCreateSessionOpen] = React.useState(false);
  const [newSessionLabel, setNewSessionLabel] = React.useState('');
  const [newSessionPasscode, setNewSessionPasscode] = React.useState('');
  const [newSessionLang, setNewSessionLang] = React.useState(i18n.language ?? 'it');
  const [newSessionType, setNewSessionType] = React.useState<'managed' | 'self-service'>('managed');
  const [createSessionLoading, setCreateSessionLoading] = React.useState(false);
  const [qrSession, setQrSession] = React.useState<UploadSession | null>(null);

  const loadSessions = React.useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await apiFetch({ operation: 'list_upload_sessions' });
      const json = await res.json();
      // RMET test sessions share this table but belong to their own tab.
      setSessions((json['output'] as UploadSession[]).filter((s) => s.type !== 'rmet'));
    } catch {
      /* ignore */
    }
    setSessionsLoading(false);
  }, [apiFetch]);

  React.useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleCreateSession = async () => {
    if (!newSessionLabel.trim() || !newSessionPasscode.trim()) return;
    setCreateSessionLoading(true);
    try {
      const res = await apiFetch({
        operation: 'create_upload_session',
        sessionLabel: newSessionLabel.trim(),
        passcode: newSessionPasscode.trim(),
        lang: newSessionLang,
        sessionType: newSessionType,
      });
      const json = await res.json();
      const passcode = newSessionPasscode.trim();
      setSessions((prev) => [{ ...(json['output'] as UploadSession), passcode }, ...prev]);
      setCreateSessionOpen(false);
      setNewSessionLabel('');
      setNewSessionPasscode('');
      setNewSessionType('managed');
    } catch {
      /* ignore */
    }
    setCreateSessionLoading(false);
  };

  const handleCloseSession = async (id: string) => {
    await apiFetch({ operation: 'close_upload_session', sessionId: id });
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, status: 'closed' } : s)));
  };

  const handleDeleteSession = async (id: string) => {
    await apiFetch({ operation: 'delete_upload_session', sessionId: id });
    setSessions((prev) => prev.filter((s) => s.id !== id));
  };

  // Keep a fresh reference to rows for use inside polling callbacks
  const rowsRef = React.useRef(rows);
  React.useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Track polling intervals per fileKey so they can be cleaned up
  const pollIntervalsRef = React.useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  React.useEffect(() => {
    const intervals = pollIntervalsRef.current;
    return () => intervals.forEach((id) => clearInterval(id));
  }, []);

  const startPolling = React.useCallback(
    (fileKey: string, fileIndex: number, openModal: boolean) => {
      const intervalId = setInterval(async () => {
        try {
          const res = await apiFetch({ fileKey, operation: 'get_recording_status' });
          if (!res.ok) return;
          const json = await res.json();
          const data: Recording = json['output'];
          const status = data.processingStatus ?? 'done';

          if (status === 'done') {
            clearInterval(pollIntervalsRef.current.get(fileKey));
            pollIntervalsRef.current.delete(fileKey);
            setFiles((prev) =>
              prev.map((f, i) => (i === fileIndex ? { ...f, status: 'done' } : f))
            );
            setRecordings([data, ...rowsRef.current.filter((r) => r.id !== data.id)]);
            if (openModal) setAnalysisModalOpen((current) => (current === null ? data : current));
          } else if (status === 'error') {
            clearInterval(pollIntervalsRef.current.get(fileKey));
            pollIntervalsRef.current.delete(fileKey);
            setFiles((prev) =>
              prev.map((f, i) => (i === fileIndex ? { ...f, status: 'error' } : f))
            );
          }
        } catch {
          // transient network error — keep polling
        }
      }, POLL_INTERVAL_MS);
      pollIntervalsRef.current.set(fileKey, intervalId);
    },
    [apiFetch, setRecordings]
  );

  /** Replace a row (and the open report, if it is that row) with fresh data. */
  const replaceRecording = React.useCallback(
    (data: Recording) => {
      setRecordings(rowsRef.current.map((r) => (r.id === data.id ? data : r)));
      setAnalysisModalOpen((current) => (current?.id === data.id ? data : current));
    },
    [setRecordings]
  );

  /** Poll a manual re-analysis until its recapStatus leaves 'pending'. */
  const pollReanalysis = React.useCallback(
    (fileKey: string) => {
      const pollKey = `recap:${fileKey}`;
      clearInterval(pollIntervalsRef.current.get(pollKey));
      const intervalId = setInterval(async () => {
        try {
          const res = await apiFetch({
            fileKey,
            operation: 'get_recording_status',
            lang: i18n.language,
          });
          if (!res.ok) return;
          const data: Recording = (await res.json())['output'];
          if (data.recapStatus === 'pending') return;
          clearInterval(pollIntervalsRef.current.get(pollKey));
          pollIntervalsRef.current.delete(pollKey);
          replaceRecording(data);
        } catch {
          // transient network error — keep polling
        }
      }, POLL_INTERVAL_MS);
      pollIntervalsRef.current.set(pollKey, intervalId);
    },
    [apiFetch, i18n.language, replaceRecording]
  );

  const startReanalysis = async () => {
    const recording = analysisModalOpen;
    if (!recording) return;
    setReanalyzeOpen(false);
    const res = await apiFetch({
      operation: 'reanalyze_recording',
      fileKey: recording.id,
      meetingPurpose: reanalyzePurpose,
    });
    if (!res.ok) {
      replaceRecording({ ...recording, recapStatus: 'error' });
      return;
    }
    replaceRecording({ ...recording, recapStatus: 'pending' });
    pollReanalysis(recording.id);
  };

  const downloadRecapPdf = async (id: string) => {
    setPdfLoading(true);
    try {
      const res = await apiFetch({
        operation: 'download_recording_pdf',
        fileKey: id,
        lang: i18n.language,
      });
      if (res.ok) {
        const { pdfBase64, filename } = (await res.json())['output'];
        downloadBase64Pdf(pdfBase64, (filename as string).split('/').pop() ?? 'recap.pdf');
      }
    } catch (error) {
      console.error('PDF download failed:', error);
    }
    setPdfLoading(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files ?? []).map((file) => ({
      file,
      numParticipants: '',
      meetingPurpose: 'unspecified' as MeetingPurpose,
      status: 'idle' as FileStatus,
    }));
    setFiles((prev) => [...prev, ...added]);
    e.target.value = '';
  };

  const updateParticipants = (index: number, value: string) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, numParticipants: value } : f)));
  };

  const updatePurpose = (index: number, value: MeetingPurpose) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, meetingPurpose: value } : f)));
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const analyzeAllMeetings = async () => {
    const snapshot = files;
    const isSingleUpload = snapshot.length === 1;
    setMeetingLoading(true);
    try {
      const presignRes = await apiFetch({
        files: snapshot.map((f) => ({ fileName: f.file.name, fileType: f.file.type })),
        operation: 'upload_recording',
      });
      const presJson = await presignRes.json();
      const urlEntries: { uploadUrl: string; fileKey: string }[] = presJson['output'];

      await Promise.allSettled(
        urlEntries.map(async ({ uploadUrl, fileKey }, i) => {
          setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: 'uploading' } : f)));
          const s3Res = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': snapshot[i].file.type },
            body: snapshot[i].file,
          });
          if (!s3Res.ok) {
            setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: 'error' } : f)));
            throw new Error(`Upload failed for ${fileKey}`);
          }

          setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: 'analyzing' } : f)));
          const response = await apiFetch({
            fileKey,
            numParticipants: Number(snapshot[i].numParticipants),
            meetingPurpose: snapshot[i].meetingPurpose,
            operation: 'analyzeMeeting',
            lang: i18n.language,
          });
          if (!response.ok) {
            setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: 'error' } : f)));
            throw new Error(`analyzeMeeting failed for ${fileKey}`);
          }
          const json = await response.json();
          const data: Recording = json['output'];

          if ((data.processingStatus ?? 'done') === 'pending') {
            // Lambda returned immediately — poll until done
            startPolling(fileKey, i, isSingleUpload);
          } else {
            // Completed synchronously (cached result or language re-analysis)
            setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: 'done' } : f)));
            setRecordings([data, ...rows.filter((r) => r.id !== data.id)]);
            if (isSingleUpload)
              setAnalysisModalOpen((current) => (current === null ? data : current));
          }
        })
      );
    } catch (error) {
      console.error('An error occurred:', error);
    }
    setMeetingLoading(false);
  };

  const isProcessing = files.some((f) => f.status === 'uploading' || f.status === 'analyzing');
  const canAnalyze =
    files.length > 0 &&
    files.every((f) => f.numParticipants !== '') &&
    !meetingLoading &&
    !isProcessing;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .filter((s) => s.id?.toLowerCase().includes(titleFilter))
        .sort((a, b) => {
          if (!a.uploadedAt && !b.uploadedAt) return 0;
          if (!a.uploadedAt) return 1;
          if (!b.uploadedAt) return -1;
          return b.uploadedAt.localeCompare(a.uploadedAt);
        })
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows, titleFilter]
  );

  const jsonAnalysis = React.useMemo(
    () => (analysisModalOpen?.analysis ? analysisModalOpen.analysis : null),
    [analysisModalOpen]
  );
  const recap = analysisModalOpen?.recap ?? null;
  // The v3 report is the default; the legacy one is shown when it is all there is, or
  // on request for records that have both.
  const viewingLegacy = !recap || showLegacy;
  const openRecordingId = analysisModalOpen?.id;
  React.useEffect(() => setShowLegacy(false), [openRecordingId]);

  return (
    <Box>
      {/* Create session dialog */}
      <Dialog
        open={createSessionOpen}
        onClose={() => setCreateSessionOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('recordings.sessions_create')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Input
              value={newSessionLabel}
              placeholder={t('recordings.sessions_label')}
              fullWidth
              onChange={(e) => setNewSessionLabel(e.target.value)}
            />
            <Input
              value={newSessionPasscode}
              placeholder={t('recordings.sessions_passcode')}
              type="password"
              fullWidth
              onChange={(e) => setNewSessionPasscode(e.target.value)}
            />
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2">{t('nav.language')}:</Typography>
              <Button
                size="small"
                variant={newSessionLang === 'it' ? 'contained' : 'outlined'}
                onClick={() => setNewSessionLang('it')}
              >
                IT
              </Button>
              <Button
                size="small"
                variant={newSessionLang === 'en' ? 'contained' : 'outlined'}
                onClick={() => setNewSessionLang('en')}
              >
                EN
              </Button>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2">{t('recordings.sessions_type')}:</Typography>
              <Button
                size="small"
                variant={newSessionType === 'managed' ? 'contained' : 'outlined'}
                onClick={() => setNewSessionType('managed')}
              >
                {t('recordings.sessions_type_managed')}
              </Button>
              <Button
                size="small"
                variant={newSessionType === 'self-service' ? 'contained' : 'outlined'}
                onClick={() => setNewSessionType('self-service')}
              >
                {t('recordings.sessions_type_self_service')}
              </Button>
              <HelpTip text={t('help.tips.recording_session_type')} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSessionOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            loading={createSessionLoading}
            disabled={!newSessionLabel.trim() || !newSessionPasscode.trim() || createSessionLoading}
            onClick={handleCreateSession}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR code dialog */}
      <Dialog fullScreen open={qrSession !== null} onClose={() => setQrSession(null)}>
        <DialogTitle>{qrSession?.label}</DialogTitle>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={4}
          gap={3}
        >
          {qrSession && (
            <>
              <QRCode value={uploadPageUrl(qrSession.id)} size={280} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: 'break-all', textAlign: 'center', maxWidth: 400 }}
              >
                {uploadPageUrl(qrSession.id)}
              </Typography>
              {qrSession.passcode && (
                <Typography variant="h6" letterSpacing={4}>
                  {t('recordings.sessions_passcode')}: {qrSession.passcode}
                </Typography>
              )}
            </>
          )}
        </Box>
        <DialogActions>
          <Button onClick={() => setQrSession(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Sessions panel */}
      <Paper sx={{ mb: 2 }}>
        <Grid
          spacing={2}
          padding={3}
          container
          direction="column"
          sx={{ alignItems: 'flex-start' }}
        >
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Typography variant="h6" sx={{ flex: 1 }}>
              {t('recordings.sessions_title')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={sessionsLoading}
              onClick={() => setCreateSessionOpen(true)}
            >
              {t('recordings.sessions_new')}
            </Button>
          </Box>
          {sessions.length === 0 && !sessionsLoading && (
            <Typography variant="body2" color="text.secondary">
              {t('recordings.sessions_empty')}
            </Typography>
          )}
          {sessions.map((s) => (
            <Box key={s.id} display="flex" alignItems="center" gap={1} width="100%" flexWrap="wrap">
              <Chip
                size="small"
                label={
                  s.status === 'open'
                    ? t('recordings.sessions_status_open')
                    : t('recordings.sessions_status_closed')
                }
                color={s.status === 'open' ? 'success' : 'default'}
              />
              <Chip
                size="small"
                label={
                  s.type === 'self-service'
                    ? t('recordings.sessions_type_self_service')
                    : t('recordings.sessions_type_managed')
                }
                variant="outlined"
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {s.label}
              </Typography>
              {isAdmin && s.createdBy && (
                <Typography variant="body2" color="text.secondary">
                  {s.createdBy}
                </Typography>
              )}
              {s.status === 'open' && (
                <>
                  <Tooltip title={t('recordings.sessions_copy_link')}>
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(uploadPageUrl(s.id))}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('recordings.sessions_qr')}>
                    <IconButton size="small" onClick={() => setQrSession(s)}>
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('recordings.sessions_close')}>
                    <IconButton size="small" onClick={() => handleCloseSession(s.id)}>
                      <LockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title={t('common.remove')}>
                <IconButton size="small" onClick={() => handleDeleteSession(s.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Grid>
      </Paper>

      {/* Analysis result dialog */}
      <Dialog
        fullScreen={true}
        maxWidth={false}
        open={analysisModalOpen !== null}
        onClose={() => setAnalysisModalOpen(null)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>ReCap Analytics · {analysisModalOpen?.id}</DialogTitle>
        {analysisModalOpen && (
          <Box paddingX={4} paddingBottom={2} style={{ textAlign: 'left' }}>
            {analysisModalOpen.recapStatus === 'pending' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('recap.reanalyze.pending')}
              </Alert>
            )}
            {analysisModalOpen.recapStatus === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('recap.reanalyze.error')}
              </Alert>
            )}
            {recap && !viewingLegacy && <RecapReportView report={recap} />}
            {viewingLegacy && jsonAnalysis && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('recap.legacy.note')}
                  {analysisModalOpen.analysisLang &&
                    analysisModalOpen.analysisLang !== i18n.language && (
                      <>
                        {' '}
                        {t('recap.legacy.other_lang', {
                          lang: analysisModalOpen.analysisLang.toUpperCase(),
                        })}
                      </>
                    )}
                </Alert>
                {deriveStatusMatrix(jsonAnalysis) && (
                  <Box display="flex" alignItems="center" gap={1} paddingBottom={2}>
                    <Typography variant="h6">{t('recordings.status_label')}</Typography>
                    <Chip
                      label={t(`transcription.status_matrix_${deriveStatusMatrix(jsonAnalysis)}`)}
                      color={STATUS_MATRIX_COLORS[deriveStatusMatrix(jsonAnalysis)!] ?? 'warning'}
                      sx={{
                        width: 170,
                        flexShrink: 0,
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                      }}
                    />
                  </Box>
                )}
                <Typography paddingBottom={2} variant="h6">
                  {jsonAnalysis.titleLine}
                </Typography>
                <Typography paddingBottom={2} variant="h6">
                  {jsonAnalysis.summaryLine}
                </Typography>
                <Typography paddingBottom={2} variant="body1">
                  {jsonAnalysis.body.join(' ')}
                </Typography>
                <Typography paddingBottom={2} variant="body1">
                  {jsonAnalysis.question}
                </Typography>
                <MatrixSection matrix={jsonAnalysis.matrix} />
              </>
            )}
          </Box>
        )}
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {recap && jsonAnalysis && (
            <Button onClick={() => setShowLegacy((v) => !v)}>
              {showLegacy ? t('recap.legacy.show_current') : t('recap.legacy.show_previous')}
            </Button>
          )}
          {analysisModalOpen && ((!recap && jsonAnalysis) || analysisModalOpen.recapOutdated) && (
            <Button
              variant="outlined"
              disabled={analysisModalOpen.recapStatus === 'pending'}
              onClick={() => {
                setReanalyzePurpose(recap?.purpose ?? 'unspecified');
                setReanalyzeOpen(true);
              }}
            >
              {t('recap.reanalyze.button')}
            </Button>
          )}
          {analysisModalOpen && recap && !viewingLegacy && (
            <Button
              variant="outlined"
              loading={pdfLoading}
              onClick={() => downloadRecapPdf(analysisModalOpen.id)}
            >
              {t('transcription.download_pdf')}
            </Button>
          )}
          {analysisModalOpen && viewingLegacy && jsonAnalysis && (
            <Button
              variant="outlined"
              onClick={() =>
                pdfMake
                  .createPdf(buildRecordingPdf(analysisModalOpen.id, jsonAnalysis, t))
                  .download(`${analysisModalOpen.id}.pdf`)
              }
            >
              {t('transcription.download_pdf')}
            </Button>
          )}
          <Button onClick={() => setAnalysisModalOpen(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Re-analysis dialog (legacy records and v3 reports from an earlier engine version) */}
      <Dialog open={reanalyzeOpen} onClose={() => setReanalyzeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('recap.reanalyze.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paddingBottom={2}>
            {t(recap ? 'recap.reanalyze.body_replace' : 'recap.reanalyze.body', {
              version: recap?.versions.analysis,
            })}
          </Typography>
          <PurposeSelect value={reanalyzePurpose} onChange={setReanalyzePurpose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReanalyzeOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={startReanalysis}>
            {t('recap.reanalyze.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper>
        <Grid
          spacing={4}
          padding={4}
          marginBottom={4}
          container
          direction="column"
          sx={{ alignItems: 'flex-start' }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <input
              id="recording-input"
              type="file"
              multiple
              accept={AUDIO_ACCEPT}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <label htmlFor="recording-input">
              <Button component="span" variant="outlined" disabled={meetingLoading || isProcessing}>
                {t('recordings.add_files')}
              </Button>
            </label>
          </Box>
          {isIOS && (
            <Typography variant="caption" color="text.secondary">
              {t('recordings.ios_voice_memos_hint')}
            </Typography>
          )}

          {files.map((entry, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              gap={2}
              sx={{ width: '100%', flexWrap: 'wrap' }}
            >
              <Typography
                variant="body2"
                sx={{
                  flexShrink: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 260,
                }}
              >
                {entry.file.name}
              </Typography>
              <Input
                value={entry.numParticipants}
                placeholder={t('recordings.num_participants')}
                type="number"
                inputProps={{ min: 1 }}
                disabled={entry.status !== 'idle'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateParticipants(i, e.target.value)
                }
                sx={{ width: 160, flexShrink: 0 }}
              />
              <PurposeSelect
                value={entry.meetingPurpose}
                disabled={entry.status !== 'idle'}
                onChange={(v) => updatePurpose(i, v)}
              />
              {entry.status !== 'idle' && (
                <Chip
                  size="small"
                  label={t(`recordings.file_status_${entry.status}`)}
                  color={statusColor(entry.status)}
                  sx={{ flexShrink: 0 }}
                />
              )}
              <Button
                size="small"
                onClick={() => removeFile(i)}
                disabled={meetingLoading || isProcessing}
                sx={{ flexShrink: 0, ml: 'auto' }}
              >
                {t('common.remove')}
              </Button>
            </Box>
          ))}

          {files.length > 0 && (
            <Button
              loading={meetingLoading}
              variant="outlined"
              onClick={analyzeAllMeetings}
              disabled={!canAnalyze}
            >
              {t('recordings.analyze_recording')}
            </Button>
          )}
        </Grid>
      </Paper>
      <Toolbar
        sx={[
          {
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          },
        ]}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h5"
          id="tableTitle"
          component="div"
          textAlign={'initial'}
        >
          {t('recordings.title')}
        </Typography>
        <Input
          id="titleFilter"
          value={titleFilter}
          placeholder={t('common.filter_by_title')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleFilter(e.target.value)}
        />
      </Toolbar>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={'small'}>
          <EnhancedTableHead
            order={'asc'}
            orderBy={'id'}
            onRequestSort={() => console.log('no sorting')}
            showOwner={isAdmin}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              return (
                <TableRecordingsRow
                  key={index}
                  row={row}
                  setAnalysisModalOpen={setAnalysisModalOpen}
                  showOwner={isAdmin}
                />
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 33 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};
